import React, { useState, useEffect } from "react";
import { X, Settings, Info } from "lucide-react";
import { fetchNodeConfigsWithFallback } from "../utils/nodeConfigApi";

const NodeConfigModal = ({ isOpen, onClose, nodeData, onSave }) => {
  const [config, setConfig] = useState({});
  const [inputs, setInputs] = useState({});
  const [isValid] = useState(true);
  const [nodeConfig, setNodeConfig] = useState(null);

  useEffect(() => {
    if (nodeData) {
      setConfig(nodeData.data?.config || {});
      setInputs(nodeData.data?.inputs || {});
      
      // ノードタイプの設定を取得
      fetchNodeConfig(nodeData.data?.nodeType);
    }
  }, [nodeData]);

  const fetchNodeConfig = async (nodeType) => {
    try {
      const { configs: allNodes } = await fetchNodeConfigsWithFallback();
      setNodeConfig(allNodes[nodeType]);
    } catch (error) {
      console.error('ノード設定の取得エラー:', error);
    }
  };

  if (!isOpen || !nodeData) return null;

  const handleInputChange = (key, value) => {
    setInputs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = () => {
    if (isValid) {
      onSave(nodeData.id, { 
        inputs,
        config
      });
      onClose();
    }
  };

  const renderConfigField = (field) => {
    const { name, type, required, options, description, placeholder, default: defaultValue } = field;
    const value = inputs[name] !== undefined ? inputs[name] : (defaultValue || "");

    switch (type) {
      case "text":
      case "string":
        return (
          <div key={name} className="mb-4">
            <label className="form-label">
              {field.description || name} {required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleInputChange(name, e.target.value)}
              className="form-input"
              placeholder={placeholder || description}
            />
            {description && (
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            )}
          </div>
        );

      case "number":
        return (
          <div key={name} className="mb-4">
            <label className="form-label">
              {field.description || name} {required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) =>
                handleInputChange(name, parseFloat(e.target.value) || 0)
              }
              className="form-input"
              placeholder={placeholder || description}
            />
            {description && (
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            )}
          </div>
        );

      case "select":
        return (
          <div key={name} className="mb-4">
            <label className="form-label">
              {field.description || name} {required && <span className="text-red-500">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleInputChange(name, e.target.value)}
              className="form-input custom-select"
            >
              <option value="">選択してください...</option>
              {options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {description && (
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={name} className="mb-4">
            <label className="form-label">
              {field.description || name} {required && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleInputChange(name, e.target.value)}
              className="form-input resize-y"
              rows={6}
              placeholder={placeholder || description}
            />
            {description && (
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            )}
          </div>
        );

      case "boolean":
        return (
          <div key={name} className="mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id={name}
                checked={value || false}
                onChange={(e) => handleInputChange(name, e.target.checked)}
                className="h-4 w-4 text-studio-600 border-gray-300 rounded focus:ring-studio-500"
              />
              <label
                htmlFor={name}
                className="ml-2 text-sm font-medium text-gray-700"
              >
                {field.description || name} {required && <span className="text-red-500">*</span>}
              </label>
            </div>
            {description && (
              <p className="text-sm text-gray-500 mt-1 ml-6">{description}</p>
            )}
          </div>
        );
      
      case "multiselect":
        return (
          <div key={name} className="mb-4">
            <label className="form-label">
              {field.description || name} {required && <span className="text-red-500">*</span>}
            </label>
            <select
              multiple
              value={Array.isArray(value) ? value : []}
              onChange={(e) => {
                const selectedOptions = Array.from(e.target.selectedOptions).map(opt => opt.value);
                handleInputChange(name, selectedOptions);
              }}
              className="form-input"
              size={Math.min(options?.length || 3, 5)}
            >
              {options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {description && (
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            )}
          </div>
        );

      case "any":
        // anyタイプは自動で前のノードから取得するので表示しない
        return null;

      default:
        return null;
    }
  };

  // ノード設定からフィールドを取得
  const configFields = nodeConfig?.inputs || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-studio-500" />
            <h3 className="text-lg font-semibold text-gray-900">
              ノード設定: {nodeData.data?.label || nodeData.type}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 設定フォーム */}
        <div className="p-4 overflow-y-auto max-h-[60vh] custom-scrollbar">
          {configFields.length > 0 ? (
            <div className="space-y-4">
              {configFields.map((field) => renderConfigField(field))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                このノードタイプには設定項目がありません
              </p>
            </div>
          )}
        </div>

        {/* フッター */}
        <div className="flex justify-end space-x-2 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className="px-4 py-2 bg-studio-500 text-white rounded-lg hover:bg-studio-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodeConfigModal;
